/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 5/5/15.
 */

'use strict';

function ViewAssetModalController(
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    uiGridConstants: JS5Object,
    DownloadAssetService: JS5Object,
    EndPointService: JS5Object,
    PermissionsService: JS5Object,
    PreviewQueryService: JS5Object,
    UtilsService: JS5Object
) {
    const PackageViewDownloadProxyViewState = 'PACKAGE_VIEW_DOWNLOAD_PROXY_VIEW_STATE';


    //=============//
    // Permissions //
    //=============//

    const getPermissions = () => {
        return PermissionsService.canRead(PackageViewDownloadProxyViewState)
            .then((canRead: boolean) => {
                this.haveViewDownloadProxyPermission = canRead;
                return this.haveViewDownloadProxyPermission;
            });
    };


    //=======//
    // Asset //
    //=======//

    this.getAssetType = () => _.toLower(this.packageContent.type);

    // this.packageContent.asset.id is a UUID and the Reach
    // inventory API doesn't like those at the moment
    const getAssetId = () => _.get(this.packageContent, 'assetId', 0);

    this.isVideoModal = () => !this.secondaryAsset && this.getAssetType() === 'video';
    this.hasPixels = () => !this.secondaryAsset && _.includes(['image', 'video'], this.getAssetType());


    //=========//
    // Preview //
    //=========//

    // use a getter for this since it's not always initialized
    this.getMediaPath = () => _.get(this.packageContent, 'mediaPath');
    const hasProxy = () => {
        return _.chain(this.packageContent)
            .get('asset.contents')
            .filter((content: JS5Object) => _.includes(
                _.toLower(content.contentUses),
                DownloadAssetService.ContentUse.Proxy))
            .value()
            .length > 0;
    }

    this.assetTypeAllowsPreview = () => !this.secondaryAsset &&
        _.includes(['image', 'video', 'audio'], this.getAssetType());
    this.showImagePreview = () => !this.secondaryAsset && _.includes(['image'], this.getAssetType());
    this.showProxyPlayer = () => !this.secondaryAsset && _.includes(['video', 'audio'], this.getAssetType());
    this.isAudio = () => !this.secondaryAsset && _.includes(['audio'], this.getAssetType());

    this.showPreviewContainer = () => (
        this.resolve.showPreview
        && !this.secondaryAsset
        && this.assetTypeAllowsPreview()
        && this.haveViewDownloadProxyPermission);

    let loadPreviewMediaUri = () => {
        if (this.haveViewDownloadProxyPermission && hasProxy() && (this.showImagePreview() || this.showProxyPlayer())) {
            DownloadAssetService.getAssetUri(getAssetId(), this.getAssetType(), DownloadAssetService.ContentUse.Proxy)
                .then((proxyUriInfo: JS5Object) => { this.packageContent.mediaPath = proxyUriInfo.uri; })
                .catch(() => { _.unset(this.packageContent, 'mediaPath'); });
        }
    };


    //================//
    // Asset Download //
    //================//

    this.canDownloadSource = () => !this.secondaryAsset && this.getAssetType() !== 'video' && getAssetId() > 0;
    this.canDownloadProxy = () => !this.secondaryAsset && hasProxy();

    const getDownloadFileName = (contentUse: string) => {
        // append e.g. 'video_proxy.mov' to the provided download
        // filename prefix
        const extension = UtilsService.getFileExtension(this.assets[_.toUpper(contentUse)].filePath);
        return this.downloadFileNamePrefix +
            `${_.toLower(this.getAssetType())}` +
            `_${_.toLower(contentUse)}` +
            `.${_.toLower(extension)}`;
    };

    const downloadAsset = (contentUse: string) => {
        if (this.haveViewDownloadProxyPermission) {
            this.loadingIndicator.show = true;
            DownloadAssetService.downloadAsset(
                getAssetId(),
                getDownloadFileName(contentUse),
                this.getAssetType(),
                contentUse
            ).then(() => {
                this.loadingIndicator.show = false;
            });
        }
    };

    this.downloadSource = () => {
        downloadAsset(DownloadAssetService.ContentUse.Source);
    };

    this.downloadProxy = () => {
        downloadAsset(DownloadAssetService.ContentUse.Proxy);
    };


    //===================//
    // Audio Tracks Grid //
    //===================//

    let colDefs = [
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableColumnResizing: false,
            field: 'trackNumber',
            cellFilter: 'addOne',
            name: 'Track',
            width: 70,
            type: 'number',
            sort: {
                direction: uiGridConstants.ASC,
                priority: 0
            },
            sortDirectionCycle: [uiGridConstants.ASC]
        },
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: 'layout',
            name: 'Layout',
            width: 200
        },
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: 'numberOfChannels',
            name: 'Channels',
            width: 95
        },
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: 'language.name',
            name: 'Language',
            width: 200
        },
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: 'role.name',
            name: 'Audio Role',
            width: 200
        },
    ];

    this.audioTracksGridOptions = {
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableSorting: true,
        rowHeight: 26,
        minRowsToShow: 8,
        columnDefs: colDefs,
        data: []
    };


    //===========//
    // Lifecycle //
    //===========//

    this.onDone = () => {
        this.close({$value: 'done'});
    };

    const getData = () => {
        let dataPromises = {
            mediaInfo: PreviewQueryService.getMediaInfo(this.packageContent.id, !this.secondaryAsset),
            haveViewDownloadProxyPermission: getPermissions()
        };

        return $q.all(dataPromises)
            .then((results: JS5Object) => {
                this.mediaInfo = _.get(results, 'mediaInfo', {});
                this.haveViewDownloadProxyPermission = _.get(results, 'haveViewDownloadProxyPermission', false);
                return loadPreviewMediaUri();
            })
            .then(() => {
                this.loadingIndicator.show = false;
            });
    };

    this.$onInit = () => {
        this.packageContent = this.resolve.packageContent;
        this.assets = [];
        _.forEach(this.packageContent.asset.contents, (content: JS5Object) => {
            this.assets[content.contentUses[0]] = content;
        });

        this.secondaryAsset = this.resolve.secondaryAsset;
        this.downloadFileNamePrefix = this.resolve.downloadFileNamePrefix;
        this.contentTemplateUrl = this.resolve.contentTemplateUrl;
        this.showPreview = this.resolve.showPreview;

        this.haveViewDownloadProxyPermission = false;
        this.audioTracksGridOptions.data = _.get(this, 'packageContent.audioTrackDescriptors', []);
        this.mediaInfo = {};

        this.loadingIndicator = {
            backdrop: true,
            show: true,
            title: 'Working',
            message: 'Please wait...'
        };
        getData();
    };
}

angular.module('prism-ui.package').component('viewAssetModal',
    {
        bindings: {
            close: '&',
            dismiss: '&',
            resolve: '<'
        },
        controller: ViewAssetModalController,
        templateUrl: `components/modal/view-asset-modal/view-asset-modal-template.html`
    });

